import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import styled from "@emotion/styled";

const zodiacSignToNumber = {
  Aries: 1,
  Taurus: 2,
  Gemini: 3,
  Cancer: 4,
  Leo: 5,
  Virgo: 6,
  Libra: 7,
  Scorpio: 8,
  Sagittarius: 9,
  Capricorn: 10,
  Aquarius: 11,
  Pisces: 12,
};

const BASE_CHART_URL = "https://aaps.space/draw-kundli/testtubechart.php";

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 380px;
`;

const ChartImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const ChartImg = styled.img`
  width: 100%;
  max-width: 280px;
  height: auto;
  border-radius: 4px;
`;

const ChartTitle = styled(Typography)`
  text-align: center;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const ControlWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%; /* match ChartImageWrapper height */
`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const ChartTab = () => {
  const [formData, setFormData] = useState({
    place: "",
    day: "",
    month: "",
    year: "",
    hour: "",
    minute: "",
  });

  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);
  const [age, setAge] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [targetYear, setTargetYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [chartData, setChartData] = useState({
    birth: { url: "", loaded: false },
    yearly: { url: "", loaded: false, loading: false },
    monthly: { urls: [], loaded: false, loading: false },
    daily: { urls: [], loaded: false, loading: false },
  });
  const [yearlyInterval, setYearlyInterval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCharts, setShowCharts] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMonthChange = async (e) => {
    const newIndex = Number(e.target.value);
    setChartData((prev) => ({
      ...prev,
      monthly: { ...prev.monthly, loading: true },
    }));

    await new Promise((resolve) => setTimeout(resolve, 300));

    setSelectedMonthIndex(newIndex);
    setChartData((prev) => ({
      ...prev,
      monthly: { ...prev.monthly, loading: false },
    }));
  };

  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setChartData((prev) => ({
      ...prev,
      daily: { ...prev.daily, loading: true },
    }));

    await new Promise((resolve) => setTimeout(resolve, 300));

    setSelectedDate(newDate);
    setChartData((prev) => ({
      ...prev,
      daily: { ...prev.daily, loading: false },
    }));
  };

  const generateChartUrls = (data, h1) => {
    try {
      const birthChartQueries = data.birthChart.chart.flatMap((entry) =>
        entry.planets.map(
          (planet) => `${planet.name.toLowerCase()}=${entry.house}`
        )
      );
      const birthChartUrl = `${BASE_CHART_URL}?h1=${h1}&brand=0&${birthChartQueries.join(
        "&"
      )}`;

      const yearlyQueries = data.yearlyChart.flatMap((house) =>
        house.planets.map((planet) => `${planet.toLowerCase()}=${house.house}`)
      );
      const yearlyChartUrl = `${BASE_CHART_URL}?h1=${h1}&brand=0&${yearlyQueries.join(
        "&"
      )}`;

      const allMonthlyUrls = data.monthlyChart.map((monthData) => {
        const monthlyQueries = monthData.chart.flatMap((house) =>
          house.planets.map(
            (planet) => `${planet.toLowerCase()}=${house.house}`
          )
        );
        return {
          url: `${BASE_CHART_URL}?h1=${h1}&brand=0&${monthlyQueries.join("&")}`,
          interval: monthData.interval,
        };
      });

      const allDailyUrls = data.dailyChart.map((dayData) => {
        const dailyQueries = dayData.chart.flatMap((house) =>
          house.planets.map(
            (planet) => `${planet.toLowerCase()}=${house.house}`
          )
        );
        return {
          url: `${BASE_CHART_URL}?h1=${h1}&brand=0&${dailyQueries.join("&")}`,
          date: dayData.date,
        };
      });

      return { birthChartUrl, yearlyChartUrl, allMonthlyUrls, allDailyUrls };
    } catch (err) {
      console.error("Error generating chart URLs:", err);
      throw new Error("Failed to generate chart URLs");
    }
  };

  const fetchChartData = async (targetYearParam) => {
    try {
      const url = targetYearParam
        ? `${process.env.REACT_APP_BACKEND_URL}astrology/yearly-chart?place=${formData.place}&day=${formData.day}&month=${formData.month}&year=${formData.year}&hour=${formData.hour}&minute=${formData.minute}&targetYear=${targetYearParam}`
        : `${process.env.REACT_APP_BACKEND_URL}astrology/birth-chart`;

      const method = targetYearParam ? "GET" : "POST";
      const body = targetYearParam
        ? undefined
        : JSON.stringify({
            place: formData.place,
            day: parseInt(formData.day),
            month: parseInt(formData.month),
            year: parseInt(formData.year),
            hour: parseInt(formData.hour),
            minute: parseInt(formData.minute),
          });

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      if (!data?.chart?.birthChart) {
        throw new Error(
          "Invalid API response structure - missing birthChart data"
        );
      }

      const firstHouse = data.chart.birthChart.chart.find(
        (entry) => entry.house === 1
      );
      if (!firstHouse)
        throw new Error("Could not find first house in birth chart");

      const h1 = zodiacSignToNumber[firstHouse.sign];
      if (!h1)
        throw new Error(
          `Invalid zodiac sign in first house: ${firstHouse.sign}`
        );

      const { birthChartUrl, yearlyChartUrl, allMonthlyUrls, allDailyUrls } =
        generateChartUrls(data.chart, h1);

      setChartData((prev) => ({
        ...prev,
        birth: { url: birthChartUrl, loaded: true },
        yearly: { url: yearlyChartUrl, loaded: true, loading: false },
        monthly: { urls: allMonthlyUrls, loaded: true, loading: false },
        daily: { urls: allDailyUrls, loaded: true, loading: false },
      }));

      setYearlyInterval(data.chart.yearInterval || null);
      setAge(data.chart.age || null);

      if (allDailyUrls.length > 0) {
        const now = new Date();
        const targetYear = targetYearParam || now.getFullYear();
        const currentDateStr = `${targetYear}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        const currentDateChart = allDailyUrls.find(
          (day) => day.date === currentDateStr
        );
        setSelectedDate(
          currentDateChart ? currentDateStr : allDailyUrls[0].date
        );
      }

      if (!targetYearParam && allMonthlyUrls.length > 0) {
        const today = new Date();
        const currentMonth = allMonthlyUrls.findIndex((month) => {
          const start = new Date(month.interval.start);
          const end = new Date(month.interval.end);
          return today >= start && today < end;
        });
        setSelectedMonthIndex(currentMonth >= 0 ? currentMonth : 0);
      }

      return true;
    } catch (err) {
      console.error("Error in fetchChartData:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch chart data"
      );
      return false;
    }
  };

  const handleYearChange = async (e) => {
    const newYear = Number(e.target.value);
    setTargetYear(newYear);
    setChartData((prev) => ({
      ...prev,
      yearly: { ...prev.yearly, loading: true },
      monthly: { ...prev.monthly, loading: true },
      daily: { ...prev.daily, loading: true },
    }));
    await fetchChartData(newYear);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setShowCharts(false);

    try {
      if (!formData.place.trim()) throw new Error("Place of birth is required");
      if (!formData.day) throw new Error("Day is required");
      if (!formData.month) throw new Error("Month is required");
      if (!formData.year) throw new Error("Year is required");
      if (!formData.hour) throw new Error("Hour is required");
      if (!formData.minute) throw new Error("Minute is required");

      const birthYear = parseInt(formData.year);
      if (
        isNaN(birthYear) ||
        birthYear < 1900 ||
        birthYear > new Date().getFullYear()
      ) {
        throw new Error("Please enter a valid birth year");
      }

      const years = Array.from(
        { length: 2099 - birthYear + 1 },
        (_, i) => birthYear + i
      );
      setAvailableYears(years);
      setTargetYear(new Date().getFullYear());

      const success = await fetchChartData();
      if (success) setShowCharts(true);
    } catch (err) {
      console.error("Error in handleSubmit:", err);
      setError(
        err instanceof Error ? err.message : "Failed to generate charts"
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedDailyChart = chartData.daily.urls.find(
    (day) => day.date === selectedDate
  );

  return (
    <Box sx={{ maxWidth: 1400, margin: "0 auto" }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {/* Place of Birth */}
            <TextField
              sx={{ flex: 3, minWidth: "150px" }}
              label="Place of Birth"
              name="place"
              value={formData.place}
              onChange={handleChange}
              required
              placeholder="Enter city name"
              error={!!error && !formData.place}
              fullWidth
            />

            {/* Day */}
            <TextField
              sx={{ flex: 1, minWidth: "80px" }}
              label="Day"
              name="day"
              type="number"
              value={formData.day}
              onChange={handleChange}
              inputProps={{ min: 1, max: 31 }}
              required
              error={!!error && !formData.day}
              fullWidth
            />

            {/* Month */}
            <TextField
              sx={{ flex: 1, minWidth: "80px" }}
              label="Month"
              name="month"
              type="number"
              value={formData.month}
              onChange={handleChange}
              inputProps={{ min: 1, max: 12 }}
              required
              error={!!error && !formData.month}
              fullWidth
            />

            {/* Year */}
            <TextField
              sx={{ flex: 1, minWidth: "100px" }}
              label="Year"
              name="year"
              type="number"
              value={formData.year}
              onChange={handleChange}
              inputProps={{ min: 1900, max: new Date().getFullYear() }}
              required
              error={!!error && !formData.year}
              fullWidth
            />

            {/* Hour */}
            <TextField
              sx={{ flex: 1, minWidth: "80px" }}
              label="Hour"
              name="hour"
              type="number"
              value={formData.hour}
              onChange={handleChange}
              inputProps={{ min: 0, max: 23 }}
              required
              error={!!error && !formData.hour}
              fullWidth
            />

            {/* Minute */}
            <TextField
              sx={{ flex: 1, minWidth: "80px" }}
              label="Minute"
              name="minute"
              type="number"
              value={formData.minute}
              onChange={handleChange}
              inputProps={{ min: 0, max: 59 }}
              required
              error={!!error && !formData.minute}
              fullWidth
            />

            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              size="large"
              sx={{ flex: 1, height: "56px", minWidth: "120px" }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Generate"
              )}
            </Button>
          </Box>
        </form>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Grid container spacing={3} sx={{ mb: 4, flexWrap: "nowrap" }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <ChartContainer>
                <LoaderWrapper>
                  <CircularProgress size={60} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Generating charts...
                  </Typography>
                </LoaderWrapper>
              </ChartContainer>
            </Grid>
          ))}
        </Grid>
      )}

      {showCharts && !loading && chartData.birth.loaded && (
        <Grid container spacing={3} sx={{ mb: 4, flexWrap: "nowrap"   }}>
          {/* Birth Chart */}
          <Grid item xs={12} sm={6} md={3}>
            <ChartContainer>
              <ChartTitle variant="subtitle1">Birth Chart</ChartTitle>
              {age !== null && (
                <Typography variant="body2" align="center" sx={{ mb: 1.5 }}>
                  Age: {age} years
                </Typography>
              )}
              <ChartImageWrapper>
                <ChartImg src={chartData.birth.url} alt="Birth Chart" />
              </ChartImageWrapper>
            </ChartContainer>
          </Grid>

          {/* Yearly Chart */}
          <Grid item xs={12} sm={6} md={3}>
            <ChartContainer>
              <ChartTitle variant="subtitle1">Yearly Chart</ChartTitle>
              <ControlWrapper>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Year</InputLabel>
                  <Select
                    value={targetYear?.toString() || ""}
                    label="Select Year"
                    onChange={handleYearChange}
                    disabled={chartData.yearly.loading}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ControlWrapper>

              <ChartImageWrapper>
                {chartData.yearly.loading ? (
                  <LoaderWrapper>
                    <CircularProgress size={40} />
                  </LoaderWrapper>
                ) : (
                  <ChartImg src={chartData.yearly.url} alt="Yearly Chart" />
                )}
              </ChartImageWrapper>
              {yearlyInterval ? (
                <Typography variant="body2" align="center" sx={{ mb: 1.5 }}>
                  {formatDate(yearlyInterval.start)} to{" "}
                  {formatDate(yearlyInterval.end)}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mb: 1.5, color: "text.secondary" }}
                >
                  {chartData.yearly.loading
                    ? "Loading interval..."
                    : "Interval data not available"}
                </Typography>
              )}
            </ChartContainer>
          </Grid>

          {/* Monthly Chart */}
          <Grid item xs={12} sm={6} md={3}>
            <ChartContainer>
              <ChartTitle variant="subtitle1">Monthly Chart</ChartTitle>
              <ControlWrapper>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Month</InputLabel>
                  <Select
                    value={selectedMonthIndex.toString()}
                    label="Select Month"
                    onChange={handleMonthChange}
                    disabled={chartData.monthly.loading}
                  >
                    {chartData.monthly.urls.map((month, idx) => {
                      const startDate = new Date(month.interval.start);
                      return (
                        <MenuItem key={idx} value={idx}>
                          {startDate.toLocaleString("default", {
                            month: "long",
                            year: "numeric",
                          })}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </ControlWrapper>
              <ChartImageWrapper>
                {chartData.monthly.loading ? (
                  <LoaderWrapper>
                    <CircularProgress size={40} />
                  </LoaderWrapper>
                ) : (
                  chartData.monthly.urls.length > 0 && (
                    <ChartImg
                      src={chartData.monthly.urls[selectedMonthIndex]?.url}
                      alt={`Monthly Chart ${selectedMonthIndex + 1}`}
                    />
                  )
                )}
              </ChartImageWrapper>
            </ChartContainer>
          </Grid>

          {/* Daily Chart */}
          <Grid item xs={12} sm={6} md={3}>
            <ChartContainer>
              <ChartTitle variant="subtitle1">Daily Chart</ChartTitle>
              <ControlWrapper>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: yearlyInterval?.start,
                    max: yearlyInterval?.end,
                  }}
                  fullWidth
                  size="small"
                  disabled={
                    chartData.daily.loading || chartData.daily.urls.length === 0
                  }
                />
              </ControlWrapper>
              <ChartImageWrapper>
                {chartData.daily.loading ? (
                  <LoaderWrapper>
                    <CircularProgress size={40} />
                  </LoaderWrapper>
                ) : selectedDailyChart ? (
                  <ChartImg src={selectedDailyChart.url} alt={`Daily Chart`} />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No chart available for selected date
                  </Typography>
                )}
              </ChartImageWrapper>
            </ChartContainer>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ChartTab;
